import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnDestroy,
    ViewChild,
    inject
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormControl } from '@angular/forms';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';

export type Column = { key: string; title: string };

export type SelectFromTableDialogData<T> = {
    title: string;
    description?: string;
    filterFunction: (searchTerm: string, dataEntry: T) => boolean;
    columns: Column[];
    data: Observable<T[]>;
    multiSelect?: boolean;
    selectAllByDefault?: boolean;
};

@Component({
    selector: 'ceres-select-service-dialog',
    templateUrl: './select-from-table-dialog.component.html',
    styleUrls: ['./select-from-table-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SelectFromTableDialogComponent<T> implements AfterViewInit, OnDestroy {
    private data = inject<SelectFromTableDialogData<T>>(MAT_DIALOG_DATA);
    readonly dialogRef = inject<MatDialogRef<SelectFromTableDialogComponent<T>>>(MatDialogRef);
    private readonly cdr = inject(ChangeDetectorRef);

    @ViewChild('searchInput') searchField!: ElementRef;

    public readonly title: string = '';
    public readonly description?: string;
    public readonly multiSelect: boolean;
    public readonly filterControl = new FormControl(null);
    public dataSource = new MatTableDataSource<T>([]);
    public selectedValue: T | undefined = undefined;
    public selectedValues: T[] = [];

    public readonly columns: Column[];

    public get columnKeys(): string[] {
        return this.columns.map((column) => column.key);
    }

    @ViewChild(MatSort)
    public sort!: MatSort;
    @ViewChild(MatPaginator)
    public paginator!: MatPaginator;

    private readonly subscription = new Subscription();
    private initializedSelection = false;

    constructor() {
        const data = this.data;

        this.subscription.add(
            combineLatest(data.data, this.filterControl.valueChanges)
                .pipe(
                    debounceTime(300),
                    map(([dataEntries, searchTerm]: [T[], string | null]) =>
                        searchTerm ? dataEntries.filter((entry) => data.filterFunction(searchTerm, entry)) : dataEntries
                    )
                )
                .subscribe((values: T[]) => {
                    this.dataSource.data = values;
                    if (data.multiSelect && data.selectAllByDefault && !this.initializedSelection) {
                        this.selectedValues = [...values];
                        this.initializedSelection = true;
                    }
                    this.cdr.markForCheck();
                })
        );
        this.title = data.title;
        this.description = data.description;
        this.multiSelect = !!data.multiSelect;
        this.columns = [{ key: 'selected', title: '' }, ...data.columns];

        this.filterControl.setValue(null); // trigger initial data load
    }

    ngAfterViewInit() {
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;

        this.subscription.add(this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0)));

        setTimeout(() => {
            this.searchField.nativeElement.focus();
        }, 500);
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    public handleApply() {
        this.dialogRef.close(this.multiSelect ? this.selectedValues : this.selectedValue);
    }

    public handleCancel() {
        this.dialogRef.close();
    }

    select(element: T, event: MatCheckboxChange) {
        if (!this.multiSelect) {
            this.selectedValue = event.checked ? element : undefined;
            return;
        }

        this.selectedValues = event.checked
            ? [...this.selectedValues, element]
            : this.selectedValues.filter((value) => value !== element);
    }

    public isSelected(element: T): boolean {
        return this.multiSelect ? this.selectedValues.includes(element) : element === this.selectedValue;
    }

    public isAllSelected(): boolean {
        return this.dataSource.data.length > 0 && this.dataSource.data.every((element) => this.isSelected(element));
    }

    public toggleAll(event: MatCheckboxChange) {
        if (event.checked) {
            this.selectedValues = Array.from(new Set([...this.selectedValues, ...this.dataSource.data]));
        } else {
            this.selectedValues = this.selectedValues.filter((value) => !this.dataSource.data.includes(value));
        }
    }
}
